import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Typography,
  Button,
  IconButton,
  Snackbar,
  Alert,
  TextField,
  useMediaQuery,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
} from "@mui/material";
import {
  AccountCircle as ProfileIcon,
  FindInPage as LostFoundIcon,
  MenuBook as ResourcesIcon,
  Help as DoubtsIcon,
  Event as EventIcon,
  Lock as LockIcon,
  DeleteOutline as DeleteIcon,
  Edit as EditIcon,
} from "@mui/icons-material";
import { Mail, School, GraduationCap, IdCard } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";

import LogoutNavbar from "../../components/LogoutNavbar";
import PostLoginFooter from "../../components/PostLoginFooter";
import GlobalSidebar from "../../components/GlobalSidebar";
import api from "../../api";
import { getFullImageUrl } from "../../middleware/Imagegetter";
import naProfile from "../../assets/images/NA.jpg";

export default function HomePage({ children } = {}) {
  const footerRef = useRef(null);
  const photoInputRef = useRef(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();

  const [user, setUser] = useState({
    name: "",
    username: "",
    student_id: "",
    email: "",
    college: "",
    year: "",
    department: "",
    image: "",
    bio: "",
    firstName: "",
    middleName: "",
    lastName: "",
  });

  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [passwordResetEmail, setPasswordResetEmail] = useState("");
  const [passwordResetSuccess, setPasswordResetSuccess] = useState(false);
  const [passwordResetError, setPasswordResetError] = useState("");
  const [isSendingPasswordReset, setIsSendingPasswordReset] = useState(false);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);

  // Snackbar state
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  // Profile photo edit state
  const [pendingPhotoFile, setPendingPhotoFile] = useState(null);
  const [pendingPhotoUrl, setPendingPhotoUrl] = useState("");
  const [isSavingPhoto, setIsSavingPhoto] = useState(false);

  const sidebarItems = [
    { 
      label: 'Profile', 
      path: '/login/student/home', 
      action: () => navigate('/login/student/home'),
      icon: <ProfileIcon />
    },
    { 
      label: 'My Lost & Found', 
      path: '/student/yourlostfound', 
      action: () => navigate('/student/yourlostfound'),
      icon: <LostFoundIcon />
    },
    { 
      label: 'My Resources', 
      path: '/student/yourskills', 
      action: () => navigate('/student/yourskills'),
      icon: <ResourcesIcon />
    },
    { 
      label: 'My Doubts', 
      path: '/student/yourdoubts', 
      action: () => navigate('/student/yourdoubts'),
      icon: <DoubtsIcon />
    },
    {
      label: 'Events',
      path: '/student/events',
      action: () => navigate('/student/events'),
      icon: <EventIcon />
    },
    {
          label: 'Know Your College',
          path: '/student/know-your-college',
          action: () => navigate('/student/know-your-college'),
          icon: <School />
        },
  ];

  const mapApiUserToState = (me) => {
    const username = (me?.username || "").trim();
    const firstNameRaw = (me?.first_name || "").trim();
    const lastNameRaw = (me?.last_name || "").trim();

    const firstNameTokens = firstNameRaw.split(/\s+/).filter(Boolean);
    const firstName = firstNameTokens[0] || "";
    const middleName = firstNameTokens.length > 1 ? firstNameTokens.slice(1).join(" ") : "";

    const displayName = ([firstNameRaw, lastNameRaw].filter(Boolean).join(" ") || username || "").trim();

    return {
      name: displayName,
      username,
      student_id: me?.student_id || "",
      email: me?.email || "",
      college: me?.college || "",
      year: me?.year || "",
      department: me?.department || "",
      image: me?.image || "",
      bio: me?.bio || "",
      firstName,
      middleName,
      lastName: lastNameRaw,
    };
  };

  useEffect(() => {
    const fetchdetails = async () => {
      try {
        const response = await api.get("users/me/");
        const me = response?.user || response;
        const nextUser = mapApiUserToState(me);
        setUser(nextUser);
      } catch (error) {
        console.error("Error occurred:", error);
      }
    };
    fetchdetails();
  }, []);

  const handleSendPasswordResetLink = async () => {
    setPasswordResetError("");

    const cleaned = (passwordResetEmail || "").trim();
    if (!cleaned) {
      setPasswordResetError("Please enter your email address.");
      return;
    }

    setIsSendingPasswordReset(true);
    try {
      await api.post("users/password-reset/", { email: cleaned, role: "student" });
      setPasswordResetSuccess(true);
      setSnackbarMessage("If an account exists for this email, a password reset link has been sent.");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
    } catch (err) {
      const msg =
        err?.error ||
        err?.detail ||
        "Unable to process your request right now. Please try again.";
      setPasswordResetError(msg);
    } finally {
      setIsSendingPasswordReset(false);
    }
  };

  const handleDeleteAccount = async () => {
    try {
      setIsDeletingAccount(true);
      const response = await api.delete("users/me/");
      setDeleteDialogOpen(false);

      setSnackbarMessage(response?.message || "Account deleted successfully.");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);

      localStorage.removeItem("token");
      localStorage.removeItem("access_token");
      localStorage.removeItem("refresh_token");
      localStorage.removeItem("accessToken");
      localStorage.removeItem("refreshToken");
      navigate("/");
    } catch (error) {
      console.error("Delete account failed:", error);
      let errorMessage = "Delete account failed. Please try again.";
      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.message) {
        errorMessage = error.message;
      }
      setSnackbarMessage(errorMessage);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setIsDeletingAccount(false);
    }
  };

  useEffect(() => {
    return () => {
      try {
        if (pendingPhotoUrl) URL.revokeObjectURL(pendingPhotoUrl);
      } catch {
        // ignore
      }
    };
  }, [pendingPhotoUrl]);

  const openPhotoPicker = () => {
    if (isSavingPhoto) return;
    photoInputRef.current?.click();
  };

  const handlePhotoSelected = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ["image/jpeg", "image/png", "image/gif", "image/jpg"];
    const maxSizeBytes = 5 * 1024 * 1024;

    if (!allowedTypes.includes(file.type)) {
      setSnackbarMessage("Please select a JPG, PNG, or GIF image.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      if (photoInputRef.current) photoInputRef.current.value = "";
      return;
    }

    if (file.size > maxSizeBytes) {
      setSnackbarMessage("Image is too large. Max size is 5MB.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      if (photoInputRef.current) photoInputRef.current.value = "";
      return;
    }

    try {
      if (pendingPhotoUrl) URL.revokeObjectURL(pendingPhotoUrl);
    } catch {
      // ignore
    }

    setPendingPhotoFile(file);
    setPendingPhotoUrl(URL.createObjectURL(file));
  };

  const cancelPendingPhoto = () => {
    try {
      if (pendingPhotoUrl) URL.revokeObjectURL(pendingPhotoUrl);
    } catch {
      // ignore
    }
    setPendingPhotoFile(null);
    setPendingPhotoUrl("");
    if (photoInputRef.current) photoInputRef.current.value = "";
  };

  const savePendingPhoto = async () => {
    if (!pendingPhotoFile || isSavingPhoto) return;

    setIsSavingPhoto(true);
    try {
      const formData = new FormData();
      formData.append("image", pendingPhotoFile);
      const response = await api.post("users/upload-photo/", formData);

      const newImage = response?.image || response?.user?.image;
      if (newImage) {
        setUser((prev) => ({ ...prev, image: newImage }));
      }

      cancelPendingPhoto();
      setSnackbarMessage(response?.message || "Profile photo updated.");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
    } catch (err) {
      const msg = err?.error || err?.detail || err?.message || "Failed to upload photo. Please try again.";
      setSnackbarMessage(msg);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setIsSavingPhoto(false);
    }
  };


  const isProfileHome = (() => {
    const path = (location?.pathname || '').toLowerCase();
    return path === '/login/student/home' || path.startsWith('/login/student/home/');
  })();

  const formUser = user;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh", bgcolor: "#f5f5f5" }}>
      <LogoutNavbar sidebarItems={isMobile ? sidebarItems : []} />
      
      <Box sx={{ display: "flex", flexGrow: 1 }}>
        {!isMobile && <GlobalSidebar />}
        
        <Box component="main" sx={{ flexGrow: 1, overflowX: "hidden" }}>
          {/* Hero Section with Profile */}
          <Box
            sx={{
              backgroundImage:
                "linear-gradient(to bottom, #1e3a5f 0%, #1e3a5f 50%, #ffffff 50%, #ffffff 100%)",
              color: "white",
              pt: { xs: 2.5, sm: 3, md: 4 },
              pb: { xs: 5, sm: 6, md: 8 },
              px: { xs: 1.5, sm: 2, md: 4 },
              position: "relative",
              overflow: "hidden",
            }}
          >
            <Box sx={{ maxWidth: "1200px", mx: "auto" }}>
              {/* <BackButton disabled tone="light" sx={{ mb: 2 }} /> */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 2, md: 3 } }}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", md: "row" },
                    alignItems: { xs: "center", md: "center" },
                    justifyContent: { xs: "center", md: "flex-start" },
                    textAlign: { xs: "center", md: "left" },
                    gap: { xs: 0, md: 2 },
                    mb: { xs: 0.5, md: 0 },
                    flexWrap: "wrap",
                  }}
                >
                  <Typography
                    variant={isMobile ? "h4" : "h3"}
                    sx={{
                      fontWeight: "bold",
                      maxWidth: "100%",
                      wordBreak: "break-word",
                      fontSize: { xs: "1.6rem", sm: "2.05rem", md: "3rem" },
                      lineHeight: 1.15,
                    }}
                  >
                    {formUser.name}
                  </Typography>
                  <Box
                    sx={{
                      bgcolor: "#10b981",
                      color: "white",
                      px: { xs: 1.25, sm: 2 },
                      py: { xs: 0.35, sm: 0.5 },
                      mt: { xs: "22px", md: 0 },
                      borderRadius: 1,
                      fontSize: { xs: "0.75rem", sm: "0.875rem" },
                      fontWeight: "bold",
                      width: "fit-content",
                      mx: { xs: "auto", md: 0 },
                    }}
                  >
                    {formUser.year}
                  </Box>
                </Box>

                <Box
                  sx={{
                    bgcolor: "white",
                    color: "#1e3a5f",
                    border: "1px solid rgba(30, 58, 95, 0.12)",
                    borderRadius: 2,
                    p: { xs: 1.5, sm: 2, md: 3 },
                  }}
                >
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", md: "220px 1fr" },
                      gap: { xs: 2, md: 3 },
                      alignItems: "stretch",
                    }}
                  >
                    {/* Left: Avatar (centered) */}
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        textAlign: "center",
                        minHeight: { xs: "auto", md: 280 },
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.75 }}>
                        <Box
                          sx={{
                            width: { xs: 120, sm: 140, md: 170 },
                            height: { xs: 120, sm: 140, md: 170 },
                            borderRadius: "50%",
                            border: { xs: "4px solid #10b981", md: "5px solid #10b981" },
                            overflow: "hidden",
                            bgcolor: "#2d5278",
                            flexShrink: 0,
                          }}
                        >
                          <img
                            src={pendingPhotoUrl || getFullImageUrl(user.image) || naProfile}
                            alt="Profile"
                            onError={(e) => {
                              e.currentTarget.onerror = null;
                              e.currentTarget.src = naProfile;
                            }}
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                              objectPosition: "center 25%",
                              display: "block",
                              imageOrientation: "from-image",
                            }}
                          />
                        </Box>

                        {isProfileHome ? (
                          <>
                            <input
                              ref={photoInputRef}
                              type="file"
                              accept="image/*"
                              hidden
                              onChange={handlePhotoSelected}
                            />
                            <IconButton
                              size="small"
                              onClick={openPhotoPicker}
                              aria-label="Edit profile photo"
                              disabled={isSavingPhoto}
                              sx={{
                                mt: "120px",
                                bgcolor: "rgba(255,255,255,0.95)",
                                color: "#1e3a5f",
                                border: "1px solid rgba(30, 58, 95, 0.18)",
                                "&:hover": { bgcolor: "#ffffff" },
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </>
                        ) : null}
                      </Box>

                      {isProfileHome && pendingPhotoFile ? (
                        <Box sx={{ display: "flex", gap: 1, mt: 1.5, flexWrap: "wrap", justifyContent: "center" }}>
                          <Button
                            variant="contained"
                            size="small"
                            onClick={savePendingPhoto}
                            disabled={isSavingPhoto}
                            sx={{
                              textTransform: "none",
                              backgroundColor: "#016f57",
                              "&:hover": { backgroundColor: "#016f57" },
                            }}
                          >
                            {isSavingPhoto ? "Saving..." : "Save"}
                          </Button>
                          <Button
                            variant="contained"
                            size="small"
                            onClick={cancelPendingPhoto}
                            disabled={isSavingPhoto}
                            sx={{
                              textTransform: "none",
                              backgroundColor: "#dc2626",
                              "&:hover": { backgroundColor: "#dc2626" },
                            }}
                          >
                            Cancel
                          </Button>
                        </Box>
                      ) : null}

                      <Typography
                        variant="h6"
                        sx={{
                          mt: 1.5,
                          fontWeight: 800,
                          fontSize: { xs: "1rem", sm: "1.05rem", md: "1.2rem" },
                          color: "#1e3a5f",
                          wordBreak: "break-word",
                        }}
                      >
                        {formUser.username || formUser.name || "Username"}
                      </Typography>
                    </Box>

                    {/* Right: Details */}
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        minWidth: 0,
                        minHeight: { xs: "auto", md: 280 },
                      }}
                    >
                      <Box
                        sx={{
                          bgcolor: "#eaeaeaae",
                          borderRadius: 2,
                          p: { xs: 1.5, sm: 2, md: 2.25 },
                          display: "flex",
                          flexDirection: "column",
                          gap: 2,
                          flexGrow: 1,
                          minWidth: 0,
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                          <Mail size={24} />
                          <Box>
                            <Typography variant="body2" sx={{ opacity: 0.8 }}>Email</Typography>
                            <Typography variant="body1" sx={{ fontWeight: "bold", wordBreak: "break-word" }}>
                              {formUser.email || "-"}
                            </Typography>
                          </Box>
                        </Box>

                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                          <GraduationCap size={24} />
                          <Box>
                            <Typography variant="body2" sx={{ opacity: 0.8 }}>University</Typography>
                            <Typography variant="body1" sx={{ fontWeight: "bold", wordBreak: "break-word" }}>
                              {formUser.college}
                            </Typography>
                          </Box>
                        </Box>
                        
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                          <School size={24} />
                          <Box>
                            <Typography variant="body2" sx={{ opacity: 0.8 }}>Department</Typography>
                            <Typography variant="body1" sx={{ fontWeight: "bold", wordBreak: "break-word" }}>
                              {formUser.department}
                            </Typography>
                          </Box>
                        </Box>
                        
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                          <IdCard size={24} />
                          <Box>
                            <Typography variant="body2" sx={{ opacity: 0.8 }}>Student ID</Typography>
                            <Typography variant="body1" sx={{ fontWeight: "bold", wordBreak: "break-word" }}>
                              {formUser.student_id}
                            </Typography>
                          </Box>
                        </Box>

                        {formUser.bio ? (
                          <Box>
                            <Typography variant="body2" sx={{ opacity: 0.8 }}>Bio</Typography>
                            <Typography variant="body1" sx={{ fontWeight: 600, wordBreak: "break-word" }}>
                              {formUser.bio}
                            </Typography>
                          </Box>
                        ) : null}
                      </Box>

                      {isProfileHome ? (
                        <Box
                          sx={{
                            display: "flex",
                            gap: 1.5,
                            pt: 1,
                            mt: { xs: 0, md: "auto" },
                            justifyContent: "flex-end",
                            flexWrap: "wrap",
                          }}
                        >
                          <Button
                            variant="outlined"
                            startIcon={<LockIcon />}
                            size="small"
                            onClick={() => {
                              setPasswordResetEmail(user.email || "");
                              setPasswordResetSuccess(false);
                              setPasswordResetError("");
                              setChangePasswordOpen(true);
                            }}
                            sx={{
                              color: "#1e3a5f",
                              borderColor: "#1e3a5f",
                              textTransform: "none",
                              borderRadius: 2,
                              px: 2,
                              py: 0.8,
                              "&:hover": {
                                bgcolor: "#f0f0f0",
                                borderColor: "#1e3a5f",
                              },
                            }}
                          >
                            Reset Password
                          </Button>

                          <Button
                            variant="outlined"
                            startIcon={<DeleteIcon />}
                            size="small"
                            onClick={() => setDeleteDialogOpen(true)}
                            sx={{
                              color: "#dc2626",
                              borderColor: "#dc2626",
                              textTransform: "none",
                              borderRadius: 2,
                              px: 2,
                              py: 0.8,
                              "&:hover": {
                                bgcolor: "#fef2f2",
                                borderColor: "#dc2626",
                              },
                            }}
                          >
                            Delete Account
                          </Button>
                        </Box>
                      ) : null}
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>

          {/* Embedded pages (no tabs / no extra container) */}
          {children ? (
            <Box sx={{ maxWidth: "1200px", mx: "auto", px: isMobile ? 2 : 4, mt: isMobile ? 2 : 3 }}>
              {children}
            </Box>
          ) : null}

          {/* Spacing before footer */}
          <Box sx={{ height: 60 }} />
        </Box>
      </Box>

      {/* Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      <div ref={footerRef}>
        <PostLoginFooter />
      </div>

      {/* Reset Password Dialog */}
      <Dialog
        open={changePasswordOpen}
        onClose={() => !isSendingPasswordReset && setChangePasswordOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Reset Password</DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Typography variant="body2" sx={{ color: "#444", mt: 1 }}>
            Enter your email address and we’ll send you a password reset link.
          </Typography>

          {passwordResetSuccess ? (
            <Alert severity="success" sx={{ mt: 2 }}>
              If an account exists for this email, a password reset link has been sent.
            </Alert>
          ) : null}

          {passwordResetError ? (
            <Alert severity="error" sx={{ mt: 2 }}>
              {passwordResetError}
            </Alert>
          ) : null}

          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
            <TextField
              label="Email"
              type="email"
              value={passwordResetEmail}
              onChange={(e) => {
                setPasswordResetEmail(e.target.value);
                if (passwordResetError) setPasswordResetError("");
              }}
              fullWidth
              disabled={isSendingPasswordReset || passwordResetSuccess}
              autoComplete="email"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Mail size={18} />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            onClick={() => setChangePasswordOpen(false)}
            disabled={isSendingPasswordReset}
            sx={{
              textTransform: "none",
              color: "#0d2a44",
              borderColor: "#0d2a44",
              "&:hover": {
                borderColor: "#0d2a44",
                backgroundColor: "rgba(13, 42, 68, 0.08)",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSendPasswordResetLink}
            disabled={isSendingPasswordReset || passwordResetSuccess}
            sx={{
              textTransform: "none",
              backgroundColor: "#0d2a44",
              "&:hover": {
                backgroundColor: "#0b2237",
              },
            }}
          >
            {isSendingPasswordReset ? "Sending..." : "Send Reset Link"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Account Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => !isDeletingAccount && setDeleteDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Account</DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Typography variant="body2" sx={{ color: "#444", mt: 1 }}>
            This action permanently deletes your account. Are you sure you want to continue?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            onClick={() => setDeleteDialogOpen(false)}
            disabled={isDeletingAccount}
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteAccount}
            disabled={isDeletingAccount}
            sx={{ textTransform: "none" }}
          >
            {isDeletingAccount ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
